import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router";
import { useIonAlert, useIonToast, IonLoading, IonIcon } from "@ionic/react";
import { Browser } from "@capacitor/browser";
import Page from "../../page-layout";
import { useAuth } from "../../../contexts/auth-context";
import { useOrderData } from "../../../providers/orderDataStore";
import {
  CreateOrderData,
  LineItem,
  ProductType,
  PaymentMethodIdEnum,
  ShippingMethodDeliveryType,
  ShippingMethodType,
  TOrder,
  ShippingMethod,
  ISODate,
} from "@core/types";
import { useGetShippingMethodsQuery } from "../../../providers/shippingAndPayment";
import { useCartData } from "../../../providers/cartDataStore";
import { useConfig } from "../../../providers/config";
import { useActiveOrMainShippingZone } from "../../../providers/shippingZones";
import { useIsViewFocused } from "../../../hooks";
import { useGetProfileQuery } from "../../../providers/profile";
import { BackButton } from "@core/components";
import { AppMetricaProvider } from "../../../providers/AppMetrica/AppMetrica";
import { useCreateOrderMutation, useCreatePaymentMutation } from "../../../providers/Orders";
import { Coupon } from "../../../providers/coupons";
import { useMarketMode } from "../../../providers/marketMode";
import useGetCurrentAddress from "../../../hooks/useGetCurrentAddress";
import { checkShippingZone } from "../../../utils/checkShippingZone";
import CartProgressBar from "../../../components/CartProgressBar";
import ProfileInfo from "../my-account/components/profile-info";
import { useTranslation } from "react-i18next";
import MaxWidthContainer from "../../../components/UI/MaxWidthContainer/MaxWidthContainer";
import { getStaticShippingMethods } from "../../../utils/getStaticShippingMethods";
import { shouldUseStaticOrderData } from "../../../utils/shouldUseStaticOrderData";
import dayjs from "dayjs";
import { deliveryDatesClient, productsClient } from "@core/clients";
import { toast } from "react-toastify";
import { getApplicableShippingMethod } from "../../../utils/getApplicableShippingMethod";
import getEnabledShippingMethods from "../../../utils/getEnabledShippingMethods";
import { ShareCartModal } from "./ShareCartModal";
import { trashOutline } from "ionicons/icons";
import { makeOrderConfirmationMessage } from "../../../utils/makeOrderConfirmationMessage";
import { useCustomerAddressesStorageData } from "../../../providers/customerAddressesStorage";
import { CartForm } from "./CartForm";
import { isFastCourierFeatureEnabled } from "../../../providers/address/taxiMasterShippingUtils";
import { useGetDeliveryShippingFeePerKg } from "../../../hooks/useGetDeliveryShippingFeePerKg";
import { useConfirmTaxiPriceChange } from "./use-confirm-taxi-price-change";

const Cart = () => {
  const { t } = useTranslation();
  const config = useConfig();
  const history = useHistory();
  const { user, isUserAuthenticated } = useAuth();
  const [showToast] = useIonToast();
  const [showLoading, setShowLoading] = useState(false);
  const [showNameConfirmation, setShowNameConfirmation] = useState(false);

  const {
    lastName,
    firstName,
    shippingMethod,
    updateOrderData,
    paymentMethod,
    deliveryDate,
    deliveryInterval,
    phoneNumber,
    customerNote,
    whatsAppNumber,
    cash_given,
  } = useOrderData();
  const [presentAlert] = useIonAlert();

  const { activeShippingZone, isLoading: isActiveShippingZoneLoading } = useActiveOrMainShippingZone();
  const activeShippingZoneId = activeShippingZone.id;
  const isViewFocused = useIsViewFocused();
  const { mode } = useMarketMode();

  const { data: profile } = useGetProfileQuery({
    userId: user?.id,
    options: {
      enabled: !!user?.id,
    },
  });

  const {
    cartSubtotalValue,
    getProductsTotalWeight,
    cartProducts,
    resetCart,
    cartTotalValue,
    coupons,
    rewardPoints,
    replaceCartProducts,
  } = useCartData();
  const totalWeight = getProductsTotalWeight();

  const shouldFetchShippingMethods =
    (!config.shipping.isShippingZonesEnabled || !!activeShippingZoneId) &&
    isViewFocused &&
    !isActiveShippingZoneLoading;

  const finishOrder = (responseData: Partial<TOrder>, requestData: CreateOrderData) => {
    AppMetricaProvider.reportEvent("orderCreate", {
      data_in_request: requestData,
      data_in_response: responseData,
    });

    resetCart(isUserAuthenticated);
    updateOrderData({
      customerNote: "",
    });

    setShowLoading(false);
    history.replace(`/thank-you/${responseData.id}`);
  };

  const { mutate: createOrderMutation } = useCreateOrderMutation({
    onSuccess(orderData, requestData) {
      if (
        config?.isOnlinePaymentEnabled &&
        orderData.paymentMethod?.type === PaymentMethodIdEnum.card &&
        orderData.id &&
        orderData.number
      ) {
        const paymentPayload = {
          amount: Math.trunc(requestData.total * 100),
          orderId: String(orderData.id),
          orderNumber: orderData.number,
        };
        createPaymentMutation(paymentPayload, {
          onSuccess(data) {
            if (data.formUrl) {
              Browser.open({ url: data.formUrl });
            }
            finishOrder(orderData, requestData);
          },
        });
      } else {
        finishOrder(orderData, requestData);
      }
    },
    onError(err) {
      console.error("❌ createOrderMutation onError", { err });
      setShowLoading(false);
      const error = err?.response?.data as any;

      const errorCode = error?.errorCode;
      const errorMessage = error?.error ?? "";

      const deliveryErrorCodes = [
        "DELIVERY_DATE_IN_PAST",
        "DELIVERY_INTERVAL_BLOCKED",
        "DELIVERY_INTERVAL_NOT_FOUND",
        "DELIVERY_INTERVAL_NOT_AVAILABLE",
      ];

      const lineItemErrorCodes = [
        "PRODUCT_NOT_FOUND",
        "PRODUCT_NOT_PURCHASABLE",
        "PRODUCT_OUT_OF_STOCK",
        "LINE_ITEM_PRICE_CHANGED",
        "LINE_ITEM_QUANTITY_CHANGED",
        "EMPTY_LINE_ITEMS",
      ];

      const message: string = t(errorMessage as keyof typeof t) ?? t("unexpectedErrorCreateOrder");

      if (errorCode && lineItemErrorCodes.includes(errorCode)) {
        handleLineItemsError();
      } else if (errorCode && deliveryErrorCodes.includes(errorCode)) {
        toast.error(message, {
          autoClose: 4000,
        });
        handleDeliveryIntervalError();
      } else {
        toast.error(message, {
          autoClose: 4000,
        });
      }
    },
  });

  const { mutate: createPaymentMutation } = useCreatePaymentMutation();

  const {
    data: fetchedShippingMethods,
    isLoading: isShippingMethodsLoading,
    isError: isShippingMethodsError,
  } = useGetShippingMethodsQuery(String(activeShippingZoneId || ""), totalWeight, {
    enabled: shouldFetchShippingMethods,
    onError: () => {
      toast.error({
        message: t("unexpectedErrorShippingMethods"),
        duration: 2000,
        color: "danger",
      });
    },
  });

  const shouldUseStaticShippingMethods = shouldUseStaticOrderData({
    staticOrderDataEnabled: config.staticOrderDataEnabled,
    staticOrderData: config.staticOrderData,
    isLoading: isShippingMethodsLoading,
    isError: isShippingMethodsError,
  });

  const shippingMethods = useMemo(() => {
    const staticShippingMethods = getStaticShippingMethods(activeShippingZoneId);
    return shouldUseStaticShippingMethods ? staticShippingMethods : fetchedShippingMethods || [];
  }, [shouldUseStaticShippingMethods, activeShippingZoneId, fetchedShippingMethods]);

  const address = useGetCurrentAddress();
  const { currentShippingAddressId } = useCustomerAddressesStorageData();

  const isWeightExceeded = totalWeight > config.maxWeightForOrderKG!;
  const isExpressMaxWeightExceeded = totalWeight > config.expressMaxWeightKg!;

  const isTaxiMasterEnabled = isFastCourierFeatureEnabled(config);
  const isTaxiMasterTurned = !!(isTaxiMasterEnabled && shippingMethod?.methodType === ShippingMethodType.taxiMaster);
  const enabledShippingMethods = getEnabledShippingMethods(shippingMethods as ShippingMethod[]);
  const isShippingsMethodsHaveTaxiMaster = enabledShippingMethods.some(
    (shipping) => shipping.methodType === ShippingMethodType.taxiMaster,
  );

  // * Вкратце напиши, что здесь происходит - хук для управления методом доставки
  useEffect(() => {
    if (!enabledShippingMethods?.length) return;

    // * Есть адрес, включен в сеттингах такси мастер и в зонах есть метод с такси мастером
    const isTaxiMasterValidOption =
      !!currentShippingAddressId && isTaxiMasterEnabled && isShippingsMethodsHaveTaxiMaster;
    // console.log("isTaxiMasterValidOption", isTaxiMasterValidOption);

    // * Текущий метод доставки - такси мастер
    const isCurrentlyTaxiMaster = shippingMethod?.methodType === ShippingMethodType.taxiMaster;
    // console.log("isCurrentlyTaxiMaster", isCurrentlyTaxiMaster);

    if (isCurrentlyTaxiMaster) {
      // * Если метод доставки такси мастер валидный, то не меняем метод доставки
      if (isTaxiMasterValidOption) {
        return;
      }
    }

    // * Фильтруем методы доставки, если метод доставки такси мастер не валидный, то удаляем его из списка
    const availableMethods = enabledShippingMethods.filter((m) => {
      if (m.methodType === ShippingMethodType.taxiMaster) {
        return false; // * taxi_master управляется только через тогл
      }
      return true;
    });
    // console.log("availableMethods", availableMethods);

    const bestMethod = getApplicableShippingMethod(availableMethods, cartSubtotalValue);
    // console.log("bestMethod", bestMethod);

    // * Если метод доставки применимый отличается от текущего, то обновляем метод доставки
    if (bestMethod && bestMethod.id !== shippingMethod?.id) {
      updateOrderData({ shippingMethod: bestMethod });
    }
  }, [
    cartSubtotalValue,
    enabledShippingMethods,
    updateOrderData,
    currentShippingAddressId,
    isTaxiMasterEnabled,
    isShippingsMethodsHaveTaxiMaster,
    shippingMethod,
  ]);

  useEffect(() => {
    if (profile) {
      const updatePayload = { firstName: profile.firstName, lastName: profile.lastName, phoneNumber: profile.phone };
      updateOrderData(updatePayload);
    }
  }, [profile]);
  const isFormValid = () => {
    const cityOrAddress = address?.city || address?.fullAddress || "";
    const shippingZoneName = activeShippingZone?.name ?? "";

    if (!isUserAuthenticated) {
      return !!(
        (
          address && // * Есть адрес
          shippingMethod && // * Есть метод доставки
          !isWeightExceeded && // * Вес заказа не превышает максимальный
          parseInt(shippingMethod.minAmount!) <= cartSubtotalValue && // * Сумма заказа не меньше минимальной
          paymentMethod && // * Есть метод оплаты
          !(isTaxiMasterTurned && (!shippingMethod.cost || shippingMethod.cost === "0")) && // * Стоимость доставки такси мастера рассчитана
          ((config.isExpressDeliveryEnabled && mode === "express" && !isExpressMaxWeightExceeded) || // * Если включена экспресс доставка и режим экспресс и вес заказа не превышает максимальный экспресс
            (isShippingsMethodsHaveTaxiMaster && isTaxiMasterTurned) || // * Если есть методы доставки с такси мастером и включена такси мастер
            shippingMethod!.methodDeliveryType === ShippingMethodDeliveryType.nextDay || // * Если метод доставки не следующий день
            (deliveryDate && deliveryInterval)) && // * Есть дата и интервал доставки
          (!config.shipping.isShippingZonesEnabled ||
            checkShippingZone(address?.city ?? address?.fullAddress ?? "", shippingZoneName))
        ) // * Если включена зона доставки или адрес в зоне доставки
      );
    }

    return !!(
      phoneNumber && // * Есть телефон
      address && // * Есть адрес
      !isWeightExceeded && // * Вес заказа не превышает максимальный
      paymentMethod && // * Есть метод оплаты
      !(isTaxiMasterTurned && (!shippingMethod?.cost || shippingMethod.cost === "0")) && // * Стоимость доставки такси мастера рассчитана
      ((config.isExpressDeliveryEnabled && mode === "express" && !isExpressMaxWeightExceeded) || // * Если включена экспресс доставка и режим экспресс и вес заказа не превышает максимальный экспресс
        (isShippingsMethodsHaveTaxiMaster && isTaxiMasterTurned) || // * Если есть методы доставки с такси мастером и включена такси мастер
        shippingMethod!.methodDeliveryType === ShippingMethodDeliveryType.nextDay || // * Если метод доставки не следующий день
        (deliveryDate && deliveryInterval)) &&
      (!config.shipping.isShippingZonesEnabled || checkShippingZone(cityOrAddress, shippingZoneName)) && // * Если включена зона доставки или адрес в зоне доставки
      parseInt(shippingMethod?.minAmount!) <= cartSubtotalValue // * Сумма заказа не меньше минимальной
    );
  };

  const handleLineItemsError = async () => {
    if (!cartProducts.length || !activeShippingZoneId) return;

    try {
      const products = await productsClient.getIncludeProducts(
        cartProducts.map((product) => String(product.id)),
        { zoneId: activeShippingZoneId },
      );

      if (products?.length) {
        replaceCartProducts(products);
        toast.success("Корзина изменилась, можете повторить попытку заказа", {
          autoClose: 6000,
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleDeliveryIntervalError = async () => {
    if (!deliveryDate) return;
    const dateIso = dayjs(deliveryDate).format("YYYY-MM-DD") as ISODate;
    if (!dateIso) return;

    try {
      const intervals = await deliveryDatesClient?.getDeliveryIntervals("_", dateIso);
      if (intervals && intervals.length > 0) {
        const nextInterval = intervals[0];
        updateOrderData({ deliveryInterval: nextInterval.text });

        toast.info("Выбран ближайший интервал для заказа", {
          autoClose: 6000,
        });
      } else {
        updateOrderData({ deliveryInterval: "" });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const feePerKg = useGetDeliveryShippingFeePerKg();
  const { confirmTaxiPriceChange } = useConfirmTaxiPriceChange();

  const submitOrder = async () => {
    if (!isFormValid()) {
      showToast({
        message: t("Please fill all required fields"),
        duration: 2000,
        color: "danger",
      });
      return;
    }

    const isTaxiPriceConfirmed = await confirmTaxiPriceChange(address);

    if (!isTaxiPriceConfirmed) {
      return;
    }

    const message = makeOrderConfirmationMessage({
      address: address,
      paymentMethodTitle: paymentMethod?.title!,
      deliveryDate: deliveryDate ?? null,
      deliveryInterval: deliveryInterval!,
      isExpressMode: config.isExpressDeliveryEnabled && mode === "express",
      isTaxiMasterTurned: isTaxiMasterTurned,
    });

    presentAlert({
      backdropDismiss: false,
      keyboardClose: false,
      header: t("orderConfirmationTitle"),
      message: message,
      buttons: [
        { text: t("orderConfirmationCancel"), role: "cancel" },
        {
          text: t("orderConfirmationConfirm"),
          role: "confirm",
          handler: async () => {
            setShowLoading(true);

            const requestData: CreateOrderData = {
              weight: totalWeight,
              deliveryDate:
                shippingMethod?.methodDeliveryType === ShippingMethodDeliveryType.default
                  ? (deliveryDate ?? undefined)
                  : undefined,
              deliveryTime:
                shippingMethod?.methodDeliveryType === ShippingMethodDeliveryType.default
                  ? deliveryInterval
                  : undefined,

              paymentMethod: paymentMethod!,
              cash_given: paymentMethod?.type === PaymentMethodIdEnum.cash ? (cash_given ?? null) : null,

              address: {
                city: address?.city ?? "",
                house: address?.house ?? "",
                isFlat: address?.isFlat,
                apartmentNumber: address?.apartmentNumber ?? "",
                street: address?.street ?? "",
                entrance: address?.entrance ?? "",
                floor: address?.floor ?? "",
                fullAddress: address?.fullAddress ?? "",
                intercom: address?.intercom ?? "",
                id: address?.id ?? "",
                longitude: address?.longitude ?? "",
                latitude: address?.latitude ?? "",
              },

              customer: {
                id: user?.id ?? "",
                firstName: firstName ?? "",
                lastName: lastName ?? "",
                phone: phoneNumber ?? "",
                whatsappNumber: whatsAppNumber ?? "",
                email: user?.email,
              },

              shippingLine: {
                methodId: shippingMethod!.methodId,
                methodTitle: shippingMethod!.title,
                shipId: shippingMethod!.id,
                total: String(parseInt(shippingMethod!.cost ?? "0") + feePerKg),
                methodType: shippingMethod!.methodType,
              },
              lineItems: cartProducts.map((cartItem) => {
                const lineItem: LineItem = {
                  id: cartItem.parentId,
                  quantity: cartItem.quantity,
                  price: cartItem.price,
                  name: cartItem.name,
                  sku: cartItem.sku,
                  portion_unit: cartItem.portionUnit,
                  unitName: cartItem.unitName,
                };

                if (cartItem.type === ProductType.VARIATION) {
                  lineItem.variationId = cartItem.id;
                }

                if (coupons.length > 0) {
                  let itemTotalPrice = cartItem.quantity * parseFloat(cartItem.price);
                  coupons.forEach((coupon) => {
                    itemTotalPrice -= Coupon.calculateDiscountForItem(coupon, cartItem, cartSubtotalValue);
                  });

                  lineItem.total = String(itemTotalPrice);
                }
                return lineItem;
              }),
              customerNote: customerNote,
              total: cartTotalValue,
              coupons: coupons,
              rewardPoints: rewardPoints ?? 0,
            };

            if (rewardPoints > 0) {
              requestData.rewardPoints = rewardPoints;
            }

            createOrderMutation(requestData);
          },
        },
      ],
    });
  };

  const handleProceedOrderSubmit = () => {
    if (!isUserAuthenticated) {
      history.push("/auth", { from: "/cart" });
      return;
    }

    if (isFormValid()) {
      if (firstName && firstName.trim().length > 0) {
        submitOrder();
      } else {
        setShowNameConfirmation(true);
      }
    }
  };

  const handleNameConfirmed = (name: string) => {
    if (name.length > 0) {
      setShowNameConfirmation(false);
      submitOrder();
    }
  };

  const handleClearCart = () => {
    presentAlert({
      header: "Очистить корзину",
      message: "Вы уверены, что хотите очистить корзину?",
      buttons: [
        { text: "Отмена", role: "cancel" },
        { text: "Очистить", role: "confirm", handler: () => resetCart(isUserAuthenticated) },
      ],
      onDidDismiss: (data) => {
        if (data.detail.role === "confirm") {
          resetCart(isUserAuthenticated);
        }
      },
    });
  };

  const showShareCartButton = !!(cartProducts.length > 0 && config.isSharedCartEnabled);

  return (
    <Page>
      <Page.Header className="pt-[env(safe-area-inset-top)] bg-white">
        <MaxWidthContainer classList="flex flex-col items-center justify-between gap-1 px-2.5 py-4 h-16">
          <div className="flex justify-between items-center w-full ">
            <BackButton />
            <div className="text-[1.25rem] font-bold">Корзина</div>
            <div className="flex items-center gap-4 mr-2 ">
              {showShareCartButton && <ShareCartModal />}
              <button onClick={handleClearCart} className="text-[0.875rem] font-medium text-gray-500 h-6 w-6">
                <IonIcon icon={trashOutline} className="text-2xl" color="danger" />
              </button>
            </div>
          </div>
        </MaxWidthContainer>
        <CartProgressBar />
      </Page.Header>
      <Page.Content scrollEvents className="my-4">
        <IonLoading isOpen={showLoading} onDidDismiss={() => setShowLoading(false)} message={t("Please wait...")} />
        <MaxWidthContainer>
          <CartForm
            isFormValid={isFormValid}
            shippingMethods={shippingMethods as ShippingMethod[]}
            onProceedOrder={handleProceedOrderSubmit}
          />
          {/* Показывается если у клиента нет имени в профиле перед отправкой заказа */}
          {showNameConfirmation && (
            <ProfileInfo
              forceOpen={true}
              showPreview={false}
              onNameConfirmed={handleNameConfirmed}
              onClose={() => setShowNameConfirmation(false)}
            />
          )}
        </MaxWidthContainer>
      </Page.Content>
    </Page>
  );
};

export default Cart;
